import { isScreenshotFixtureMode } from '@/shared/lib/screenshotMode'
import { supabase } from '@/shared/supabase/client'
import type { Database } from '@/shared/supabase/database.types'

type SignedDocRow = Pick<
  Database['public']['Tables']['signed_documents']['Row'],
  | 'id'
  | 'document_type'
  | 'source_table'
  | 'source_id'
  | 'signature_timestamp'
  | 'verification_url'
  | 'signed_pdf_url'
  | 'patient_id'
>

export interface SignedDocumentListItem {
  id: string
  documentType: string
  sourceTable: string | null
  sourceId: string | null
  signatureTimestamp: string
  verificationUrl: string | null
  /** Path no bucket `medical-documents` (coluna `signed_pdf_url`). */
  signedPdfStoragePath: string | null
  patientId: string | null
}

const mapRow = (r: SignedDocRow): SignedDocumentListItem => ({
  id: r.id,
  documentType: r.document_type,
  sourceTable: r.source_table,
  sourceId: r.source_id,
  signatureTimestamp: r.signature_timestamp,
  verificationUrl: r.verification_url,
  signedPdfStoragePath: r.signed_pdf_url,
  patientId: r.patient_id,
})

/** Ids por consulta em `listForClinicalDocuments` (STAT-03). */
export const CLINICAL_DOCUMENT_STATUS_BATCH = 100

export const signedDocumentsRepository = {
  /**
   * Última assinatura ligada a um documento clínico (`clinical_documents`).
   * Usado para reimprimir o PDF assinado sem gerar de novo nem reassinar.
   */
  getLatestForClinicalDocument: async (clinicalDocumentId: string): Promise<SignedDocumentListItem | null> => {
    if (isScreenshotFixtureMode()) return null
    const id = clinicalDocumentId.trim()
    if (!id) return null
    const { data, error } = await supabase
      .from('signed_documents')
      .select(
        'id, document_type, source_table, source_id, signature_timestamp, verification_url, signed_pdf_url, patient_id',
      )
      .eq('source_table', 'clinical_documents')
      .eq('source_id', id)
      .order('signature_timestamp', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) {
      throw new Error(error.message)
    }
    if (!data) return null
    return mapRow(data as SignedDocRow)
  },

  /**
   * STAT-03 — assinaturas de uma lista de documentos clínicos, sem o teto de 100 de `listMine`.
   * Consulta em lotes de `CLINICAL_DOCUMENT_STATUS_BATCH` ids (limite prático da URL do `in`).
   * Pode devolver mais de uma linha por documento (reassinatura); quem chama escolhe a mais recente.
   */
  listForClinicalDocuments: async (clinicalDocumentIds: string[]): Promise<SignedDocumentListItem[]> => {
    if (isScreenshotFixtureMode()) return []
    const ids = [...new Set(clinicalDocumentIds.map((id) => id.trim()).filter(Boolean))]
    if (!ids.length) return []
    const batches: string[][] = []
    for (let i = 0; i < ids.length; i += CLINICAL_DOCUMENT_STATUS_BATCH) {
      batches.push(ids.slice(i, i + CLINICAL_DOCUMENT_STATUS_BATCH))
    }
    const results = await Promise.all(
      batches.map(async (batch) => {
        const { data, error } = await supabase
          .from('signed_documents')
          .select(
            'id, document_type, source_table, source_id, signature_timestamp, verification_url, signed_pdf_url, patient_id',
          )
          .eq('source_table', 'clinical_documents')
          .in('source_id', batch)
          .order('signature_timestamp', { ascending: false })
        if (error) throw new Error(error.message)
        return (data as SignedDocRow[] | null)?.map(mapRow) ?? []
      }),
    )
    return results.flat()
  },

  /** RLS: apenas linhas com `doctor_id = auth.uid()`. */
  listMine: async (limit = 25): Promise<SignedDocumentListItem[]> => {
    const { data, error } = await supabase
      .from('signed_documents')
      .select(
        'id, document_type, source_table, source_id, signature_timestamp, verification_url, signed_pdf_url, patient_id',
      )
      .order('signature_timestamp', { ascending: false })
      .limit(Math.min(Math.max(limit, 1), 100))

    if (error) {
      throw new Error(error.message)
    }
    return (data as SignedDocRow[] | null)?.map(mapRow) ?? []
  },

  /**
   * Total de documentos assinados do médico autenticado.
   * RLS já restringe a `doctor_id = auth.uid()` — sem filtro manual.
   * Erro é propagado (não mascarado como 0) para a tela distinguir
   * "zero assinados" de "falha ao contar".
   */
  countMine: async (): Promise<number> => {
    if (isScreenshotFixtureMode()) return 0
    const { count, error } = await supabase
      .from('signed_documents')
      .select('id', { count: 'exact', head: true })

    if (error) {
      throw new Error(error.message)
    }
    return count ?? 0
  },

  /**
   * URL temporária para leitura (RLS do Storage aplica-se ao utilizador autenticado).
   *
   * PROPAGA o erro (`throw`) em vez de devolver `null`: um `null` silencioso era indistinguível
   * de "documento nunca assinado" para quem chama, e `tryServeSignedFromStorage` (em
   * `prescriptionPdf.ts`) usava exatamente essa ambiguidade para cair, sem perceber, no PDF local
   * sem validade jurídica quando a URL falhava por rede/RLS (DOC-01, design §1.2/§3.1).
   */
  createSignedPdfDownloadUrl: async (storagePath: string, expiresSec = 3600): Promise<string> => {
    const path = storagePath.trim()
    if (!path) {
      throw new Error('Caminho de armazenamento do PDF assinado está vazio.')
    }
    const { data, error } = await supabase.storage
      .from('medical-documents')
      .createSignedUrl(path, Math.min(Math.max(expiresSec, 60), 86_400))

    if (error || !data?.signedUrl) {
      throw new Error(error?.message || 'Falha ao gerar URL assinada do Storage.')
    }
    return data.signedUrl
  },
}
